import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { useTheme } from '../../context/ThemeContext';

/**
 * Enhanced ErrorBoundary component for graceful error handling
 * Catches JavaScript errors anywhere in the child component tree
 * Displays a fallback UI instead of crashing the entire application
 * Includes automatic retry, error reporting, and recovery mechanisms
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
      errorId: null,
      lastErrorTime: null
    };
    
    // Bind methods
    this.handleRetry = this.handleRetry.bind(this);
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      lastErrorTime: Date.now()
    };
  }

  componentDidCatch(error, errorInfo) {
    // Enhanced error logging with more context
    const enhancedError = {
      message: error.message,
      stack: error.stack,
      name: error.name,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.state.retryCount,
      errorId: this.state.errorId,
      props: this.props.logProps ? this.props : undefined
    };
    
    console.error('ErrorBoundary caught an error:', enhancedError);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Report error to monitoring service if available
    if (this.props.onError) {
      this.props.onError(enhancedError, errorInfo);
    }

    // Auto-retry for certain error types if enabled
    if (this.props.autoRetry && this.shouldAutoRetry(error)) {
      const retryDelay = Math.min(1000 * Math.pow(2, this.state.retryCount), 10000);
      setTimeout(() => {
        if (this.state.retryCount < (this.props.maxRetries || 3)) {
          this.handleRetry();
        }
      }, retryDelay);
    }
  }

  shouldAutoRetry(error) {
    // Auto-retry for network errors, chunk load errors, etc.
    const retryableErrors = [
      'ChunkLoadError',
      'Loading chunk',
      'Network Error',
      'Failed to fetch'
    ];
    
    return retryableErrors.some(errorType => 
      error.message?.includes(errorType) || error.name?.includes(errorType)
    );
  }

  handleRetry() {
    const maxRetries = this.props.maxRetries || 3;
    
    if (this.state.retryCount >= maxRetries) {
      console.warn('Max retries reached, not retrying');
      return;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      lastErrorTime: Date.now()
    }));
  }

  handleReset() {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorId: null,
      lastErrorTime: null
    });
  }

  componentDidUpdate(prevProps) {
    // Reset error boundary when key props change
    if (this.props.resetOnPropsChange && 
        this.state.hasError && 
        this.props.resetKeys?.some(key => prevProps[key] !== this.props[key])) {
      this.handleReset();
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error, 
          this.handleRetry, 
          this.handleReset,
          {
            retryCount: this.state.retryCount,
            maxRetries: this.props.maxRetries || 3,
            errorId: this.state.errorId,
            canRetry: this.state.retryCount < (this.props.maxRetries || 3)
          }
        );
      }

      // Default fallback UI
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onReset={this.handleReset}
          onNavigateBack={this.props.onNavigateBack}
          retryCount={this.state.retryCount}
          maxRetries={this.props.maxRetries || 3}
          errorId={this.state.errorId}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Enhanced error fallback component with better UX and error details
 */
const ErrorFallback = ({ 
  error, 
  errorInfo,
  onRetry, 
  onReset,
  onNavigateBack, 
  retryCount = 0, 
  maxRetries = 3,
  errorId,
  showDetails = false
}) => {
  const { isDarkMode } = useTheme();

  const getErrorMessage = () => {
    if (error?.message?.includes('ChunkLoadError') || error?.message?.includes('Loading chunk')) {
      return {
        title: 'App Update Available',
        message: 'A new version of the app is available. Please refresh to get the latest updates.',
        suggestion: 'This usually happens when we release new features or improvements.',
        severity: 'medium',
        icon: 'update'
      };
    }

    if (error?.message?.includes('Network') || error?.message?.includes('Failed to fetch')) {
      return {
        title: 'Connection Problem',
        message: 'Unable to connect to our servers. Please check your internet connection.',
        suggestion: 'Make sure you have a stable internet connection and try again.',
        severity: 'high',
        icon: 'network'
      };
    }

    if (error?.name === 'TypeError' && error?.message?.includes('Cannot read')) {
      return {
        title: 'Data Loading Error',
        message: 'There was a problem loading the required data for this page.',
        suggestion: 'This might be a temporary issue. Please try refreshing the page.',
        severity: 'medium',
        icon: 'data'
      };
    }

    return {
      title: 'Something went wrong',
      message: 'An unexpected error occurred while loading this page.',
      suggestion: 'Please try again or contact support if the problem persists.',
      severity: 'high',
      icon: 'general'
    };
  };

  const errorDetails = getErrorMessage();
  const canRetry = retryCount < maxRetries;

  const getIcon = () => {
    switch (errorDetails.icon) {
      case 'update':
        return (
          <svg className="h-8 w-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'network':
        return (
          <svg className="h-8 w-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'data':
        return (
          <svg className="h-8 w-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />;
    }
  };

  const getBgColor = () => {
    switch (errorDetails.icon) {
      case 'update':
        return 'bg-blue-100 dark:bg-blue-900/30';
      case 'network':
        return 'bg-orange-100 dark:bg-orange-900/30';
      case 'data':
        return 'bg-purple-100 dark:bg-purple-900/30';
      default:
        return 'bg-red-100 dark:bg-red-900/30';
    }
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center p-4">
      <div className="max-w-lg mx-auto text-center">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6`}>
          <div className="flex justify-center mb-6">
            <div className={`p-3 ${getBgColor()} rounded-full`}>
              {getIcon()}
            </div>
          </div>
          
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {errorDetails.title}
          </h3>
          
          <p className="text-gray-600 dark:text-gray-300 mb-2 text-sm sm:text-base leading-relaxed">
            {errorDetails.message}
          </p>
          
          <p className="text-gray-500 dark:text-gray-400 mb-6 text-xs sm:text-sm">
            {errorDetails.suggestion}
          </p>

          {errorId && (
            <p className="text-gray-400 dark:text-gray-500 mb-4 text-xs font-mono">
              Error ID: {errorId}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {canRetry && onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-purple-600 hover:bg-purple-700 
                         text-white font-medium rounded-lg text-sm sm:text-base
                         hover:shadow-lg hover:-translate-y-0.5
                         transition-all duration-200 ease-out
                         focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 
                         dark:focus:ring-offset-gray-800 min-h-[44px]"
              >
                <svg 
                  className="w-4 h-4 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
                Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
              </button>
            )}
            
            {onNavigateBack && (
              <button
                onClick={onNavigateBack}
                className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-gray-600 hover:bg-gray-700 
                         text-white font-medium rounded-lg text-sm sm:text-base
                         hover:shadow-lg hover:-translate-y-0.5
                         transition-all duration-200 ease-out
                         focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 
                         dark:focus:ring-offset-gray-800 min-h-[44px]"
              >
                <span className="hidden sm:inline">Go Back</span>
                <span className="sm:hidden">Back</span>
              </button>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-amber-600 hover:bg-amber-700 
                       text-white font-medium rounded-lg text-sm sm:text-base
                       hover:shadow-lg hover:-translate-y-0.5
                       transition-all duration-200 ease-out
                       focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 
                       dark:focus:ring-offset-gray-800 min-h-[44px]"
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
              <span className="hidden sm:inline">Refresh Page</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>

          {onReset && (
            <div className="mt-4">
              <button
                onClick={onReset}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 
                         underline hover:no-underline transition-colors duration-200"
              >
                Reset Error State
              </button>
            </div>
          )}
          
          {(process.env.NODE_ENV === 'development' || showDetails) && error && (
            <details className="mt-6 text-left">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 mb-2">
                Technical Details {process.env.NODE_ENV === 'development' && '(Development Mode)'}
              </summary>
              <div className="space-y-2">
                <div className="text-xs text-red-600 dark:text-red-400 bg-gray-100 dark:bg-gray-900 p-2 rounded">
                  <strong>Error:</strong> {error.name}: {error.message}
                </div>
                {error.stack && (
                  <pre className="text-xs text-red-600 dark:text-red-400 bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto max-h-32">
                    {error.stack}
                  </pre>
                )}
                {errorInfo?.componentStack && (
                  <pre className="text-xs text-orange-600 dark:text-orange-400 bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto max-h-32">
                    Component Stack:{errorInfo.componentStack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;